/**
 * TimezoneCombobox — searchable IANA timezone picker.
 *
 * Uses Intl.supportedValuesOf('timeZone') when available (Chrome 99+, Safari 17+,
 * Firefox 125+) and falls back to a curated canonical list for older runtimes.
 * Always emits a valid IANA string (e.g. "Asia/Kuala_Lumpur"), never "GMT+8".
 */
import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronDown, Globe } from 'lucide-react';

// Curated fallback — covers the most common zones so users always see choices.
const FALLBACK_TZS = [
  'UTC',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
  'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota',
  'America/Chicago', 'America/Denver', 'America/Halifax', 'America/Lima',
  'America/Los_Angeles', 'America/Mexico_City', 'America/New_York',
  'America/Phoenix', 'America/Sao_Paulo', 'America/Toronto', 'America/Vancouver',
  'Asia/Bangkok', 'Asia/Dhaka', 'Asia/Dubai', 'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi',
  'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Riyadh',
  'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Taipei',
  'Asia/Tashkent', 'Asia/Tehran', 'Asia/Tokyo',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Melbourne',
  'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
  'Europe/Bucharest', 'Europe/Dublin', 'Europe/Istanbul', 'Europe/Lisbon',
  'Europe/London', 'Europe/Madrid', 'Europe/Moscow', 'Europe/Oslo',
  'Europe/Paris', 'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm',
  'Europe/Warsaw', 'Europe/Zurich',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
];

const getAllZones = () => {
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      const zones = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length) return zones;
    }
  } catch { /* ignore */ }
  return FALLBACK_TZS;
};

const offsetLabel = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const off = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return off.replace('GMT', 'UTC') || '';
  } catch {
    return '';
  }
};

const TimezoneCombobox = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select timezone…',
  testId = 'tz-combobox',
}) => {
  const [open, setOpen] = useState(false);
  const zones = useMemo(getAllZones, []);
  const friendly = (tz) => {
    if (!tz) return '';
    const off = offsetLabel(tz);
    return off ? `${tz} (${off})` : tz;
  };

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            <Globe className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="truncate">
              {value ? friendly(value) : <span className="text-muted-foreground">{placeholder}</span>}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search city or region…" data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {zones.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={(v) => {
                    onChange?.(v);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-item-${tz.replace(/[/+]/g, '-')}`}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 ${value === tz ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="flex-1 truncate">{tz}</span>
                  <span className="text-[10px] text-muted-foreground ml-2">{offsetLabel(tz)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default TimezoneCombobox;
